import {
  addDays,
  addMinutes,
  format,
  isBefore,
  setHours,
  setMinutes,
  startOfDay,
  parseISO,
} from "date-fns";
import { Booking, StaffAssignment, DaySchedule } from "../types";

export interface DateSlot {
  date: Date;
  dateStr: string;
  dayName: string;
  dayNum: string;
  monthName: string;
  isClosed: boolean;
}

// Enterprise Constants
export const DEFAULT_START_HOUR = 9;
export const DEFAULT_END_HOUR = 17;

/**
 * 5-Minute Safety Buffer: Accounts for processing time, race conditions,
 * and network latency during the booking flow.
 */
export const BOOKING_BUFFER_MINUTES = 5;

/**
 * Enterprise Slot Engine: Generates business-rule compliant dates for booking.
 * Supports business-specific schedule filtering and closed-date marking.
 */
export function generateDateSlots(
  daysToGenerate = 30,
  scheduleOrDays?:
    | Record<string, { isOpen: boolean; start: string; end: string }>
    | string[],
  closedDates: string[] = [],
  minDuration = 30,
  isAdmin = false,
): DateSlot[] {
  // Guard: Normalize empty schedule objects to undefined to ensure system
  // defaults apply for new businesses without a configured schedule.
  if (
    scheduleOrDays &&
    typeof scheduleOrDays === "object" &&
    !Array.isArray(scheduleOrDays) &&
    Object.keys(scheduleOrDays).length === 0
  ) {
    scheduleOrDays = undefined;
  }

  const slots: DateSlot[] = [];
  const now = new Date();
  const today = startOfDay(now);

  let dateOffset = 0;

  for (let i = 0; i < daysToGenerate; i++) {
    const date = addDays(today, i + dateOffset);
    const dateStr = format(date, "yyyy-MM-dd");
    const dayNameShort = format(date, "EEE"); // Sun, Mon...

    let isClosed = closedDates.includes(dateStr);

    const daySchedule =
      scheduleOrDays &&
      typeof scheduleOrDays === "object" &&
      !Array.isArray(scheduleOrDays)
        ? scheduleOrDays[dayNameShort]
        : undefined;

    // Default: Friday is closed in many regions if no schedule provided
    if (!scheduleOrDays && dayNameShort === "Fri") {
      isClosed = true;
    }

    // STRICT MODE: If schedule is provided but this day is missing, mark as closed.
    // This prevents "mystery bookings" on unconfigured days.
    if (
      scheduleOrDays &&
      typeof scheduleOrDays === "object" &&
      !Array.isArray(scheduleOrDays) &&
      !daySchedule
    ) {
      isClosed = true;
    }

    if (daySchedule) {
      isClosed = isClosed || !daySchedule.isOpen;
    }

    // VANISH PATTERN: If this is the potential "Today", check if the shift is over.
    if (i === 0 && dateOffset === 0 && !isClosed) {
      let closingHour = DEFAULT_END_HOUR;
      let closingMin = 0;

      if (daySchedule && daySchedule.end) {
        [closingHour, closingMin] = daySchedule.end.split(":").map(Number);
      }

      const closeTime = setMinutes(setHours(today, closingHour), closingMin);

      // Shift Over detection: Skip today if the remaining time cannot accommodate
      // the safety buffer AND the minimum service duration.
      const bufferMinutes = isAdmin ? 0 : BOOKING_BUFFER_MINUTES;
      const minPossibleEnd = addMinutes(now, bufferMinutes + minDuration);

      if (isBefore(closeTime, minPossibleEnd)) {
        // Shift is over! Skip today and start the calendar from tomorrow.
        dateOffset = 1;
        // Recalculate date for the new i=0
        const nextDate = addDays(today, i + dateOffset);
        const nextDateStr = format(nextDate, "yyyy-MM-dd");
        const nextDayName = format(nextDate, "EEE");

        // Re-check closure status for the new date
        const nextDaySchedule =
          scheduleOrDays &&
          typeof scheduleOrDays === "object" &&
          !Array.isArray(scheduleOrDays)
            ? scheduleOrDays[nextDayName]
            : undefined;

        const nextIsClosed =
          closedDates.includes(nextDateStr) ||
          (nextDaySchedule ? !nextDaySchedule.isOpen : false) ||
          (!scheduleOrDays && nextDayName === "Fri");

        slots.push({
          date: nextDate,
          dateStr: nextDateStr,
          dayName: nextDayName,
          dayNum: format(nextDate, "d"),
          monthName: format(nextDate, "MMMM"),
          isClosed: nextIsClosed,
        });
        continue;
      }
    }

    // Working Days Array Logic
    if (scheduleOrDays && Array.isArray(scheduleOrDays)) {
      if (!scheduleOrDays.includes(dayNameShort)) {
        isClosed = true;
      }
    }

    slots.push({
      date,
      dateStr,
      dayName: dayNameShort,
      dayNum: format(date, "d"),
      monthName: format(date, "MMMM"),
      isClosed,
    });
  }
  return slots;
}

/**
 * Vanish Pattern: Identifies if a slot is in the past or within the 5-minute booking buffer.
 * Ensures absolute prevention of double-booking or retro-booking.
 */
export function isSlotInPast(
  timeStr: string,
  dateStr: string,
  isAdmin = false,
): boolean {
  if (!timeStr || !dateStr) return true;

  const now = new Date();
  const [hours, minutes] = timeStr.split(":").map(Number);

  // Construct the target date object from the date string and slot time
  const targetDate = parseISO(dateStr);
  const slotDate = setMinutes(setHours(targetDate, hours), minutes);

  // Safety Buffer: Prevents booking slots that are too close to current time
  // Admin bypass: 0 minute buffer for staff
  const bufferMinutes = isAdmin ? 0 : BOOKING_BUFFER_MINUTES;
  const safetyBuffer = addMinutes(now, bufferMinutes);

  return isBefore(slotDate, safetyBuffer);
}

/**
 * Availability Engine: Generates 15-minute time intervals between start and end hours.
 * Excludes the end hour as a starting slot.
 */
export function generateTimeSlots(
  start: number | string,
  end: number | string,
  serviceDurationMinutes: number = 30,
): string[] {
  const slots: string[] = [];

  // Handle both number (9) and string ("09:00") inputs
  const startH =
    typeof start === "number" ? start : parseInt(start.split(":")[0]);
  const startM = typeof start === "number" ? 0 : parseInt(start.split(":")[1]);
  const endH = typeof end === "number" ? end : parseInt(end.split(":")[0]);
  const endM = typeof end === "number" ? 0 : parseInt(end.split(":")[1]);

  let current = setMinutes(setHours(new Date(), startH), startM);
  const endTime = setMinutes(setHours(new Date(), endH), endM);

  // Enterprise Logic: A slot is only valid if its end time (current + duration)
  // does not exceed the business closing time.
  while (true) {
    const slotEndTime = addMinutes(current, serviceDurationMinutes);
    if (isBefore(endTime, slotEndTime)) break;

    slots.push(format(current, "HH:mm"));
    current = addMinutes(current, 15); // Maintain 15-minute start-time grid
  }

  return slots;
}

/** The scheduler's grid resolution. Slot start times are multiples of this. */
export const SLOT_INTERVAL_MINUTES = 15;

/** Fallback when a booking or service carries no usable duration. */
export const DEFAULT_SERVICE_DURATION_MINUTES = 30;

/**
 * How much of the calendar a booking actually occupies.
 *
 * Mirrors `public.booking_occupied_minutes()` in SQL and must stay in step
 * with it: rounded up to the 15-minute grid, never less than one interval.
 *
 * The floor is the important part. A booking whose window is zero minutes
 * wide satisfies no overlap test -- `newStart < otherEnd && newEnd > otherStart`
 * is false against itself at the same instant -- so a 0-minute booking used to
 * be invisible to every conflict check and could be stacked without limit onto
 * a stylist who was already busy. Zero and null both mean "no duration
 * recorded" and fall back to the 30-minute default, which is also what the
 * roster grid has always *displayed* for such bookings.
 */
export function occupiedMinutes(durationMinutes?: number | null): number {
  const base =
    durationMinutes && durationMinutes > 0
      ? durationMinutes
      : DEFAULT_SERVICE_DURATION_MINUTES;
  return Math.max(
    Math.ceil(base / SLOT_INTERVAL_MINUTES) * SLOT_INTERVAL_MINUTES,
    SLOT_INTERVAL_MINUTES,
  );
}

/**
 * Does this line take up time on a stylist's calendar?
 *
 * A product is a thing handed over, not an appointment. It is the only kind of
 * line that legitimately adds zero minutes -- a *service* recorded as zero
 * minutes is a catalogue mistake, and treating the two the same is what let a
 * 0-minute "Shampoo" booking sit invisibly on top of a stylist who was already
 * with a client.
 */
export function isTimedService(service: {
  item_type?: string | null;
}): boolean {
  return service.item_type !== "product";
}

/**
 * A booking has to be an appointment with somebody.
 *
 * Products are add-ons: sold to a client who is already in the chair, never on
 * their own. Without this rule a basket of pure products still has to reserve
 * a slot (nothing can occupy zero minutes -- see `occupiedMinutes`), so it
 * would block a stylist's calendar for a transaction that needs none of her
 * time, and the day would read as full when it is not.
 *
 * Deliberately a front-end rule, not a database constraint: this is temporary
 * policy that holds until products get their own checkout, and a constraint
 * would have to be migrated away again to lift it.
 */
export function hasTimedService(services: { item_type?: string | null }[]) {
  return services.some(isTimedService);
}

/**
 * Total calendar time a basket of services needs.
 *
 * Products and explicitly zero-duration lines add nothing to the sum -- a
 * retail item does not extend the appointment -- but the total is then floored
 * by `occupiedMinutes`, because the client is still standing in front of a
 * stylist for however long it takes.
 */
export function computeServicesDuration(
  services: {
    item_type?: string | null;
    duration_minutes?: number | null;
    buffer_minutes?: number | null;
  }[],
): number {
  const sum = services.reduce(
    (acc, s) =>
      acc +
      (isTimedService(s)
        ? (s.duration_minutes ?? DEFAULT_SERVICE_DURATION_MINUTES) +
          (s.buffer_minutes && s.buffer_minutes > 0 ? s.buffer_minutes : 0)
        : 0),
    0,
  );
  return occupiedMinutes(sum);
}

/**
 * The appointment length as the client experiences it: what they're told,
 * what their confirmation says, what "the visit takes about X minutes"
 * means. Deliberately excludes buffer_minutes.
 *
 * Buffer is calendar padding for the stylist -- room to run over or clean up
 * without bumping the next client -- not part of the service being
 * performed. Use `computeServicesDuration` (buffer-inclusive) for anything
 * that drives what the calendar actually reserves; use this one for
 * anything the client reads.
 */
export function computeVisibleDuration(
  services: {
    item_type?: string | null;
    duration_minutes?: number | null;
  }[],
): number {
  const sum = services.reduce(
    (acc, s) =>
      acc +
      (isTimedService(s)
        ? (s.duration_minutes ?? DEFAULT_SERVICE_DURATION_MINUTES)
        : 0),
    0,
  );
  return occupiedMinutes(sum);
}

export function timeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + (minutes || 0);
}

export interface AvailableSlotMatch {
  date: string;
  time: string;
  workerId: string;
}

/**
 * Client-side availability finder to suggest the next free slot for a specific duration.
 * Scans only the target date. Obeys business schedules, safety buffer, and tie-breaking order.
 */
export function findNextAvailableSlots(
  bookings: Booking[],
  staffList: StaffAssignment[],
  targetDateStr: string,
  durationMinutes: number,
  businessSchedule: Record<string, DaySchedule> | null,
  preferredWorkerId?: string | null,
): AvailableSlotMatch | null {
  // Same floor as the server: a zero-length window would "fit" everywhere,
  // including on top of a stylist who is already booked.
  const span = occupiedMinutes(durationMinutes);
  const [year, month, day] = targetDateStr.split("-").map(Number);
  const dateObj = new Date(year, month - 1, day);
  const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
    dateObj.getDay()
  ];
  const daySchedule = businessSchedule && businessSchedule[dayName];

  let start = "09:00";
  let end = "18:00";

  if (daySchedule && !daySchedule.isOpen) {
    return null;
  }
  if (daySchedule) {
    start = daySchedule.start || "09:00";
    end = daySchedule.end || "18:00";
  }

  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);

  const now = new Date();
  const todayStr = format(now, "yyyy-MM-dd");
  let startSearchMinutes = startMin;

  if (targetDateStr === todayStr) {
    // Align to the 15-minute grid FIRST, then step forward only if the
    // aligned slot is too close to be filled in and submitted.
    //
    // This used to add a flat 6-minute buffer before aligning, which quietly
    // cost a whole slot most of the time: at 13:12 the buffer pushed to 13:18
    // and the ceiling rounded that to 13:30, skipping a 13:15 that the roster
    // grid was showing as open and that `get_available_slots` would have
    // accepted (its admin path applies no lead buffer at all). "Earliest
    // opening" naming a later time than the grid in front of the worker
    // reads as a bug in one of the two, and staff-side scheduling is exactly
    // where last-minute and walk-in bookings are supposed to land.
    //
    // A small lead is still needed: `check_booking_overlap` rejects any
    // confirmed booking whose start is already in the past, so suggesting a
    // slot 20 seconds out just produces a temporal_buffer_violation after the
    // form is filled in.
    const MIN_LEAD_MINUTES = 2;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    let gridAlignedMinutes = Math.ceil(nowMinutes / 15) * 15;
    if (gridAlignedMinutes - nowMinutes < MIN_LEAD_MINUTES) {
      gridAlignedMinutes += 15;
    }
    startSearchMinutes = Math.max(startMin, gridAlignedMinutes);
  }

  // Filter bookings to target date and active status
  const activeBookings = bookings.filter(
    (b) => b.requested_date === targetDateStr && b.status === "confirmed",
  );

  // Determine active staff
  const activeStaff = staffList.filter((s) => s.is_active);
  if (activeStaff.length === 0) {
    activeStaff.push({ id: "unassigned", name: "General" } as StaffAssignment);
  }

  // Group active bookings by worker
  const workerBookingsMap: Record<string, { start: number; end: number }[]> =
    {};
  staffList.forEach((s) => {
    workerBookingsMap[s.id] = [];
  });
  if (!workerBookingsMap["unassigned"]) {
    workerBookingsMap["unassigned"] = [];
  }

  activeBookings.forEach((b) => {
    const workerKey = b.worker_id || "unassigned";
    const startB = timeToMinutes(b.requested_time);
    const dur = occupiedMinutes(b.duration_minutes);
    if (!workerBookingsMap[workerKey]) {
      workerBookingsMap[workerKey] = [];
    }
    workerBookingsMap[workerKey].push({ start: startB, end: startB + dur });
  });

  // Determine tie-breaker search order: preferred staff first, then others
  let searchOrder = [...activeStaff];
  if (preferredWorkerId) {
    const selected = activeStaff.find((s) => s.id === preferredWorkerId);
    if (selected) {
      searchOrder = [selected];
    }
  }

  // Scan chronologically first to find the earliest slot overall
  for (
    let s = startSearchMinutes;
    s <= endMin - span;
    s += SLOT_INTERVAL_MINUTES
  ) {
    for (const staff of searchOrder) {
      const busySegments = workerBookingsMap[staff.id] || [];
      const slotStart = s;
      const slotEnd = s + span;

      // Check if slot overlaps with any busy segment
      const hasConflict = busySegments.some(
        (seg) => slotStart < seg.end && slotEnd > seg.start,
      );

      if (!hasConflict) {
        // Found a match! Format minutes back to HH:mm
        const hours = Math.floor(slotStart / 60);
        const minutes = slotStart % 60;
        const timeStr = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;

        return {
          date: targetDateStr,
          time: timeStr,
          workerId: staff.id,
        };
      }
    }
  }

  return null;
}

/**
 * Default fallback for how far ahead of "now" a CLIENT must book, in minutes.
 * Overridden dynamically by business.min_lead_minutes on tenant storefronts.
 *
 * Mirrors server rules in `get_available_slots` and `check_booking_overlap`.
 * Staff are deliberately exempt -- the dashboard books walk-ins.
 */
export const CLIENT_LEAD_MINUTES = 15;

/**
 * Is this slot beyond a client's reach right now -- already started, OR too
 * close to now for the server to accept it?
 *
 * Replaces a check that only asked "has it started yet". When a slot falls
 * inside the business's lead notice window, the server omits it from the
 * available list. Collapsing both cases here ensures unreachable slots are
 * cleanly greyed out rather than offering impossible waitlists.
 */
export function isSlotBeyondClientReach(
  time: string,
  date: string,
  utcOffsetMinutes: number,
  now: number = Date.now(),
  leadMinutes: number = CLIENT_LEAD_MINUTES,
): boolean {
  const offsetMs = utcOffsetMinutes * 60 * 1000;
  const slotMs =
    new Date(`${date}T${time.slice(0, 5)}:00Z`).getTime() - offsetMs;
  if (Number.isNaN(slotMs)) return false;
  return now + leadMinutes * 60 * 1000 >= slotMs;
}

export interface SlotAvailabilityInfo {
  slot_time: string;
  is_available: boolean;
  is_waitlistable: boolean;
  capacity?: number;
  booked_count?: number;
}
